"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { fetcher } from "@/lib/http/fetcher";
import type { ProductForClient } from "@/lib/shared/types/interfaces";
import type { IPaginatedResult, ISortCriteria } from "@/lib/shared/types/pagination";
import { productKeys } from "../../shared/keys";

interface UseProductsParams {
  labels: string[];
  page?: number;
  sort?: ISortCriteria;
  enabled?: boolean;
  initialData?: IPaginatedResult<ProductForClient>;
  initialSort?: ISortCriteria;
  initialDataCurrencyCode?: string;
  priceFilter?: {
    countryCode?: string;
    currencyCode: string;
    maxPrice: number;
  };
}

export function useProducts({
  labels,
  page = 1,
  sort,
  enabled = true,
  initialData,
  initialSort,
  initialDataCurrencyCode,
  priceFilter,
}: UseProductsParams) {
  const locale = useLocale();
  const defaultSort = initialSort ?? {
    criteria: "priority" as const,
    order: "asc" as const,
  };
  const effectiveSort = sort ?? defaultSort;

  return useQuery<IPaginatedResult<ProductForClient>>({
    queryKey: productKeys.list(labels, locale, page, effectiveSort, priceFilter),
    queryFn: () => {
      const params = new URLSearchParams({
        locale,
        page: page.toString(),
      });
      if (labels.length > 0) {
        params.set("labels", labels.join(","));
      }
      if (priceFilter) {
        params.set("maxPrice", priceFilter.maxPrice.toString());
        params.set("currencyCode", priceFilter.currencyCode);
        if (priceFilter.countryCode) {
          params.set("countryCode", priceFilter.countryCode);
        }
      }
      params.append("sortCriteria", effectiveSort.criteria);
      params.append("sortOrder", effectiveSort.order);
      const url = `/${locale}/api/products?${params.toString()}`;
      return fetcher(url);
    },
    enabled: enabled && (labels.length > 0 || priceFilter != null),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: priceFilter ? undefined : keepPreviousData,
    initialData:
      initialData &&
      page === 1 &&
      effectiveSort.criteria === defaultSort.criteria &&
      effectiveSort.order === defaultSort.order &&
      (!priceFilter ||
        initialDataCurrencyCode?.toUpperCase() === priceFilter.currencyCode.toUpperCase())
        ? initialData
        : undefined,
  });
}
